/**
 * Core type definitions for the 3DS SDK
 */

/**
 * Backend payment response format
 * Supports two flows:
 * 1. Redirect flow: uses redirect.url, redirect.method, redirect.data
 * 2. Action flow: uses action and value.payLoad
 */
export interface PaymentResponse {
  /** Redirect information for 3DS authentication (for redirect flow) */
  redirect?: Redirect;
  /** Action URL and value for form submission (for action flow) */
  action?: string;
  /** Value containing payload and other data (for action flow) */
  value?: AuthenticationValue;
  /** Original transaction status */
  OriginalTxnStatus?: string;
  /** Original transaction status code */
  OriginalTxnStatusCode?: number;
  /** Description of the response */
  description?: string;
  /** Response message */
  respMsg?: string;
  /** Transaction amount */
  txnAmount?: string;
  /** Response code */
  respCode?: number;
  /** Status */
  status?: string;
  /** Status code */
  statusCode?: number;
  /** Response code */
  responseCode?: number;
}

/**
 * Redirect information for form submission
 */
export interface Redirect {
  /** Base64 encoded JSON data for form submission */
  data: string;
  /** HTTP method for form submission (e.g., 'POST', 'GET') */
  method: string;
  /** URL to submit the form to */
  url: string;
}

/**
 * Authentication value for form submission flow
 */
export type AuthenticationValue = Record<string, unknown>;

/**
 * Authentication flow using action and value structure
 */
export interface AuthenticationFlow {
  /** Action URL to submit the form to */
  action: string;
  /** Value containing payload and other data */
  value: AuthenticationValue;
}

// TODO Discussed with the team, we will not use the data field in the payment response.
/**
 * 3DS authentication result
 */
export interface ThreeDSAuthResult {
  /** Whether authentication was successful */
  success: boolean;
  /** Error message if authentication failed */
  error?: string;
  /** Additional response data */
  data?: Record<string, unknown>;
}

/**
 * SDK configuration options
 */
export interface SDKConfig {
  /** Timeout in milliseconds for modal operations */
  timeout?: number;
  /** Enable debug logging */
  debug?: boolean;
  /** Custom modal container selector (optional) */
  modalContainer?: string;
  /** Modal width in pixels or 'fullscreen' for full screen mode (default: 600) */
  modalWidth?: number | 'fullscreen';
  /** Modal height in pixels or 'fullscreen' for full screen mode (default: 720) */
  modalHeight?: number | 'fullscreen';
  /** Callback when authentication starts */
  onStart?: () => void;
  /** Callback when authentication completes */
  onComplete?: (result: ThreeDSAuthResult) => void;
  /** Callback when authentication fails */
  onError?: (error: Error) => void;
  /**Show close button */
  showClose?: boolean;
  /** Custom CSS class to apply to the modal container for custom styling */
  customClass?: string;
}

/**
 * SDK error class
 */
export class SDKError extends Error {
  constructor(
    message: string,
    public code?: string,
    public details?: unknown
  ) {
    super(message);
    this.name = 'SDKError';
    Object.setPrototypeOf(this, SDKError.prototype);
  }
}
